import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Loader, MantineProvider, Tabs, createTheme, mergeThemeOverrides } from "@mantine/core";
import ColorInput from "@/components/atoms/ColorInput";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { customTheme } from "@/mantine-theme";
import { brandColorTuple } from "@/utils/brandColors";
import { useBranding } from "@/contexts/BrandingContext";
import {
  deleteBrandLogo,
  postBrandLogo,
  putUpdateWhiteLabelProperties,
  useGetBrandLogo,
  useGetWhiteLabelProperties,
  useWhiteLabelContext,
} from "@/services/WhiteLabel";
import { SettingsFormContent } from ".";
import { useTranslation } from "react-i18next";
import SidebarLayoutNavItem, { SidebarLayoutNavRoot } from "@/components/atoms/SidebarLayout";
import Checkbox from "@/components/atoms/Checkbox";
import Divider from "@/components/atoms/Divider";
import Progress from "@/components/atoms/Progress";
import Select from "@/components/atoms/Select";
import Switch from "@/components/atoms/Switch";
import Text from "@/components/atoms/Text";
import Heading from "@/components/atoms/Heading";
import Stack from "@/components/atoms/Stack";
import Slider from "@/components/atoms/Slider";
import CTA from "@/components/atoms/CTA";
import TextInput from "@/components/atoms/TextInput";
import Group from "@/components/atoms/Group";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import Dropzone from "@/components/molecules/Dropzone";
import Image from "@/components/molecules/Image";

const AQUA_DEFAULT = "#1aefd6";
const RADIUS_DEFAULT = (customTheme.defaultRadius ?? 2) as number;

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
];

export default function BrandingSettings() {
  const { t } = useTranslation();
  const { branding, setBranding } = useBranding();
  const queryClient = useQueryClient();
  const [primaryColor, setPrimaryColor] = useState(branding?.primaryColor ?? AQUA_DEFAULT);
  const [borderRadius, setBorderRadius] = useState(branding?.borderRadius ?? RADIUS_DEFAULT);
  const [date, setDate] = useState<Date | null>(new Date());
  // const [primaryGradientFrom, setPrimaryGradientFrom] = useState("#edffc8");
  // const [primaryGradientMiddle, setPrimaryGradientMiddle] = useState("#ffffff");
  // const [primaryGradientTo, setPrimaryGradientTo] = useState("#8cf7eb");
  // const [secondaryGradientFrom, setSecondaryGradientFrom] = useState("#c6fbf5");
  // const [secondaryGradientTo, setSecondaryGradientTo] = useState("#84e7dc");

  const { whiteLabelId } = useWhiteLabelContext();
  const { data: whiteLabelData } = useGetWhiteLabelProperties(whiteLabelId!, {
    enabled: whiteLabelId != null,
  });
  const initialized = useRef(false);
  useEffect(() => {
    if (!whiteLabelData || initialized.current) return;
    initialized.current = true;

    const p = whiteLabelData.properties;
    if (p["theme.primaryColor"]) setPrimaryColor(p["theme.primaryColor"]);
    if (p["theme.radius"]) setBorderRadius(Number(p["theme.radius"]));

    // const primaryGradient = p["theme.subheader.primary"];
    // if (primaryGradient) {
    //   const colors = primaryGradient.match(/#[0-9a-fA-F]{6}/g) ?? [];
    //   if (colors[0]) setPrimaryGradientFrom(colors[0]);
    //   if (colors[1]) setPrimaryGradientMiddle(colors[1]);
    //   if (colors[2]) setPrimaryGradientTo(colors[2]);
    // }

    // const secondaryGradient = p["theme.subheader.secondary"];
    // if (secondaryGradient) {
    //   const colors = secondaryGradient.match(/#[0-9a-fA-F]{6}/g) ?? [];
    //   if (colors[0]) setSecondaryGradientFrom(colors[0]);
    //   if (colors[1]) setSecondaryGradientTo(colors[1]);
    // }
  }, [whiteLabelData]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      putUpdateWhiteLabelProperties(whiteLabelId!, {
        ...whiteLabelData?.properties,
        "theme.primaryColor": primaryColor,
        "theme.radius": String(borderRadius),
      }),
    onSuccess: () => {
      setBranding({ primaryColor, borderRadius });
      notifications.show({ message: "Merkevare lagret", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["getWhiteLabelProperties", whiteLabelId!] });
      queryClient.invalidateQueries({ queryKey: ["whiteLabelContext"] });
    },
  });

  // const { mutate: saveSubHeader, isPending: isSavingSubHeader } = useMutation({
  //   mutationFn: () =>
  //     putUpdateWhiteLabelProperties(whiteLabelId!, {
  //       ...whiteLabelData?.properties,
  //       "theme.subheader.primary": `linear-gradient(120deg, ${primaryGradientFrom} 30%, ${primaryGradientMiddle} 50%, ${primaryGradientTo} 70%)`,
  //       "theme.subheader.secondary": `linear-gradient(120deg, ${secondaryGradientFrom}, ${secondaryGradientTo})`,
  //     }),
  //   onSuccess: () => {
  //     notifications.show({ message: "SubHeader lagret", color: "green" });
  //     queryClient.invalidateQueries({ queryKey: ["getWhiteLabelProperties", whiteLabelId!] });
  //   },
  // });

  const [logoFiles, setLogoFiles] = useState<File[]>([]);

  const { data: brandLogo, isLoading: isLoadingLogo } = useGetBrandLogo(whiteLabelId!, {
    enabled: whiteLabelId != null,
  });

  const { mutate: uploadLogo, isPending: isUploadingLogo } = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return postBrandLogo(whiteLabelId!, formData);
    },
    onSuccess: () => {
      setLogoFiles([]);
      notifications.show({ message: "Logo lagret", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["getBrandLogo", whiteLabelId!] });
      queryClient.invalidateQueries({ queryKey: ["whiteLabelContext"] });
    },
  });

  const { mutate: removeLogo, isPending: isRemovingLogo } = useMutation({
    mutationFn: () => deleteBrandLogo(whiteLabelId!),
    onSuccess: () => {
      notifications.show({ message: "Logo slettet", color: "green" });
      queryClient.invalidateQueries({ queryKey: ["getBrandLogo", whiteLabelId!] });
      queryClient.invalidateQueries({ queryKey: ["whiteLabelContext"] });
    },
  });

  const previewTheme = useMemo(
    () =>
      mergeThemeOverrides(
        customTheme,
        createTheme({
          primaryColor: "brand",
          colors: { brand: brandColorTuple(primaryColor || AQUA_DEFAULT) },
          defaultRadius: borderRadius,
        })
      ),
    [primaryColor, borderRadius]
  );

  return (
    <Stack>
      <Box mt="xl">
        <Heading order={2} styleOrder={4} tt="uppercase">
          Logo
        </Heading>
        <SettingsFormContent>
          {isLoadingLogo ? (
            <Loader size="sm" />
          ) : (
            <Stack gap="md">
              {brandLogo && <Image src={brandLogo} mah={80} maw={240} fit="contain" />}
              <Group align="flex-end">
                <Dropzone
                  origin="local"
                  type="button"
                  files={logoFiles}
                  setFiles={setLogoFiles}
                  maxNumberOfFiles={1}
                  buttonLabel={t("actions:upload")}
                  accept={["image/png", "image/jpeg", "image/svg+xml"]}
                  isLoading={isUploadingLogo}
                />
                {logoFiles.length > 0 && (
                  <CTA intent="primary" onClick={() => uploadLogo(logoFiles[0])} loading={isUploadingLogo}>
                    {t("actions:save")}
                  </CTA>
                )}
                {brandLogo && (
                  <CTA intent="tertiary" onClick={() => removeLogo()} loading={isRemovingLogo}>
                    {t("actions:delete")}
                  </CTA>
                )}
              </Group>
            </Stack>
          )}
        </SettingsFormContent>
      </Box>

      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.branding")}
      </Heading>

      <SettingsFormContent>
        <Grid>
          <Grid.Col span={{ base: 12, xs: 6 }}>
            <ColorInput
              label={t("settings:branding.brandColor")}
              value={primaryColor}
              onChange={setPrimaryColor}
              format="hex"
              swatches={["#1aefd6", "#039355", "#DD3310", "#0f40fe", "#fed829", "#a61e05"]}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 6 }} pb={24}>
            <Slider
              label={t("settings:branding.borderRadius")}
              min={0}
              max={20}
              step={1}
              value={borderRadius}
              onChange={setBorderRadius}
              marks={[
                { value: 0, label: "0" },
                { value: 5, label: "5" },
                { value: 10, label: "10" },
                { value: 15, label: "15" },
                { value: 20, label: "20" },
              ]}
            />
          </Grid.Col>
        </Grid>
        <Group mt="sm">
          <CTA intent="primary" onClick={() => save()} loading={isPending}>
            {t("actions:save")}
          </CTA>
        </Group>
      </SettingsFormContent>

      <Box mt="xl">
        <Heading order={3} styleOrder={5} tt="uppercase" mb="md">
          {t("settings:branding.preview")}
        </Heading>
        <MantineProvider theme={previewTheme} cssVariablesSelector="#branding-preview">
          <Box
            id="branding-preview"
            data-mantine-color-scheme="light"
            p="xl"
            style={
              {
                border: "1px solid var(--color-border-primary)",
                borderRadius: 4,
                "--radius": `${borderRadius}px`,
                // Preview always reflects the edited brand color in the nav accent.
                "--nav-active-bg": "var(--mantine-primary-color-light)",
                "--nav-active-border": "var(--mantine-primary-color-filled)",
              } as React.CSSProperties
            }
          >
            <Stack gap="xl">
              {/* Section 1: Form controls */}
              <Stack gap="xs">
                <Text size="xs" display="technical" tt="uppercase" fw={400} mb="md">
                  Forms
                </Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Stack>
                      <TextInput label="Name" placeholder="Enter your name" radius={borderRadius} />
                      <TextInput
                        label="Name (focused)"
                        placeholder="Enter your name"
                        radius={borderRadius}
                        styles={{
                          input: {
                            borderColor: "var(--mantine-primary-color-filled)",
                          },
                        }}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Stack>
                      <Select label="Status" placeholder="Select status" data={statusOptions} radius={borderRadius} />
                      <Select
                        label="Status (focused)"
                        placeholder="Select status"
                        data={statusOptions}
                        radius={borderRadius}
                        styles={{
                          input: {
                            borderColor: "var(--mantine-primary-color-filled)",
                          },
                        }}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Stack>
                      <DatePickerInput
                        label="Date"
                        value={date}
                        onChange={(x) => setDate(x instanceof Date ? x : null)}
                        radius={borderRadius}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Slider
                      label="Quantity"
                      min={0}
                      max={100}
                      value={60}
                      onChange={() => {}}
                      marks={[
                        { value: 0, label: "0%" },
                        { value: 50, label: "50%" },
                        { value: 100, label: "100%" },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>

              <Divider />

              {/* Section 2: Actions & status */}
              <Stack gap="xs">
                <Text size="xs" display="technical" tt="uppercase" fw={400} mb="md">
                  Actions &amp; status
                </Text>
                <Stack gap="md">
                  <Group gap="sm">
                    <CTA intent="primary">Primary</CTA>
                    <CTA intent="secondary">Secondary</CTA>
                    <CTA intent="tertiary">Tertiary</CTA>
                  </Group>
                  <Group gap="xl">
                    <Checkbox label="Unchecked" color="brand" />
                    <Checkbox label="Checked" color="brand" defaultChecked />
                    <Switch label="Off" color="brand" />
                    <Switch label="On" color="brand" defaultChecked />
                  </Group>
                </Stack>
              </Stack>

              <Divider />

              {/* Section 3: Navigation & feedback */}
              <Stack gap="xs">
                <Text size="xs" display="technical" tt="uppercase" fw={400} mb="md">
                  Navigation &amp; feedback
                </Text>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Stack gap="md">
                      <Tabs defaultValue="overview" color="brand">
                        <Tabs.List>
                          <Tabs.Tab value="overview">Overview</Tabs.Tab>
                          <Tabs.Tab value="details">Details</Tabs.Tab>
                          <Tabs.Tab value="history">History</Tabs.Tab>
                        </Tabs.List>
                      </Tabs>
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="sm">Storage used</Text>
                          <Text size="sm">40%</Text>
                        </Group>
                        <Progress value={40} color="brand" radius={borderRadius} />
                        <Group justify="space-between">
                          <Text size="sm">Tasks completed</Text>
                          <Text size="sm">75%</Text>
                        </Group>
                        <Progress value={75} color="brand" radius={borderRadius} />
                      </Stack>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <SidebarLayoutNavRoot>
                      <SidebarLayoutNavItem href="/settings/branding" icon="user" text="Account" />
                      <SidebarLayoutNavItem href="/settings/branding" icon="companyXl" text="Company" isActive />
                      <SidebarLayoutNavItem href="/settings/branding" icon="economyXl" text="Economy" />
                      <SidebarLayoutNavItem href="/settings/branding" icon="products" text="Products" />
                    </SidebarLayoutNavRoot>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Stack>
          </Box>
        </MantineProvider>
      </Box>

      {/* TODO: this one can wait */}
      {/* <Box mt="xl">
        <Heading order={3} styleOrder={5} tt="uppercase" mb="md">
          SubHeader
        </Heading>
        <SettingsFormContent>
          <Grid>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Text size="xs" display="technical" tt="uppercase" fw={400} mb="md">
                Primary gradient
              </Text>
              <Group>
                <ColorInput label="From" value={primaryGradientFrom} onChange={setPrimaryGradientFrom} format="hex" />
                <ColorInput
                  label="Middle"
                  value={primaryGradientMiddle}
                  onChange={setPrimaryGradientMiddle}
                  format="hex"
                />
                <ColorInput label="To" value={primaryGradientTo} onChange={setPrimaryGradientTo} format="hex" />
              </Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Text size="xs" display="technical" tt="uppercase" fw={400} mb="md">
                Secondary gradient
              </Text>
              <Group>
                <ColorInput
                  label="From"
                  value={secondaryGradientFrom}
                  onChange={setSecondaryGradientFrom}
                  format="hex"
                />
                <ColorInput label="To" value={secondaryGradientTo} onChange={setSecondaryGradientTo} format="hex" />
              </Group>
            </Grid.Col>
          </Grid>
          <Group mt="sm">
            <CTA intent="primary" onClick={() => saveSubHeader()} loading={isSavingSubHeader}>
              Lagre
            </CTA>
          </Group>
        </SettingsFormContent>
        <Box
          mt="md"
          style={
            {
              "--gradient-primary": `linear-gradient(120deg, ${primaryGradientFrom} 30%, ${primaryGradientMiddle} 50%, ${primaryGradientTo} 70%)`,
              "--gradient-secondary": `linear-gradient(120deg, ${secondaryGradientFrom}, ${secondaryGradientTo})`,
            } as React.CSSProperties
          }
        >
          <Stack gap="md">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                Trade header
              </Text>
              <TradeHeader companyName={currentCompany?.companyName ?? ""} orders={[]} createNew={() => {}} />
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                Contracts header
              </Text>
              <ContractsHeader
                companyName={currentCompany?.companyName ?? ""}
                sortOptions={[]}
                totalContracts={12}
                isFetchingContracts={false}
                onClickNewContract={() => {}}
                status={"ACTIVE" as ContractStatus}
              />
            </div>
          </Stack>
        </Box>
      </Box> */}
    </Stack>
  );
}
